import { z } from 'zod';

/**
 * Cross-app read of one campaign's members (Context: "Launcher HTTP reads" —
 * `GET /api/tools/launcher/campaigns/:id/members`, `.passthrough()` since
 * only a handful of the fully-denormalized row's columns are used here).
 *
 * Extracted out of `components/ScanReview.tsx` so the parse/map step is a
 * plain, importable function — same pattern as `campaign-window.ts` and
 * `submit-validation.ts` in this app.
 *
 * Terra never has Lead-backed members (Spencer-confirmed, R8) — the schema
 * carries no `lead_id`/`lead_name` fields, so there is nothing to fall back
 * to; a member row with no `contact_id` maps to `contactId: null`.
 */
export const CampaignMemberSchema = z
  .object({
    id: z.string(),
    contact_id: z.string().nullable().optional(),
    contact_name: z.string().nullable().optional(),
    contact_email: z.string().nullable().optional(),
    contact_title: z.string().nullable().optional(),
    account_name: z.string().nullable().optional(),
    /**
     * The SFDC `CampaignMember.Status` value ("Sent", "Responded",
     * "Attended", …). The launcher handler is a bare `SELECT *` over
     * `launcher_sfdc_campaign_members`, so the wire key is that table's
     * column name — `cm_status`, not `status`. (Terra does alias the same
     * column to a bare `status` in `src/services/flow/recent-sends.ts`, but
     * that is Flow's read, not this endpoint's.) Nullable, and it can also
     * hold the Terra-invented `'Removed'` sentinel that
     * `src/services/launcher-member-sync.ts` stamps on members that
     * disappeared from SFDC but still have dispatch history.
     */
    cm_status: z.string().nullable().optional(),
  })
  .passthrough();

export const CampaignMembersResponseSchema = z.array(CampaignMemberSchema);

export interface CampaignMember {
  id: string;
  contactId: string | null;
  name: string;
  title: string | null;
  accountName: string | null;
  email: string | null;
  /** SFDC `CampaignMember.Status` — see `cm_status` on the schema above. */
  status: string | null;
}

/** Maps the parsed `/campaigns/:id/members` rows to the app's view model. */
export function mapCampaignMembers(rows: z.output<typeof CampaignMembersResponseSchema>): CampaignMember[] {
  return rows.map(
    (m): CampaignMember => ({
      id: m.id,
      contactId: m.contact_id ?? null,
      name: m.contact_name ?? 'Unknown',
      title: m.contact_title ?? null,
      accountName: m.account_name ?? null,
      email: m.contact_email ?? null,
      status: m.cm_status ?? null,
    }),
  );
}

/**
 * Cross-app write of `POST /campaigns/:id/sync-members` (Context: "Launcher
 * HTTP reads" — same auth middleware as the members GET above, registered
 * in `mini-apps/launcher/api.ts`). Live-QA round-3 bug 2: `GET
 * /campaigns/:id/members` reads ONLY the launcher's local mirror table
 * (`launcher_sfdc_campaign_members`), which nothing populates for a campaign
 * never opened in the launcher UI — so the "Existing campaign member"
 * search in `ScanReview.tsx` came up empty for exactly the campaigns a rep
 * is most likely to be scanning at. This sync route runs a real synchronous
 * SFDC sync and returns `{synced, member_count, synced_at}`; `ScanReview`
 * fires it once per campaign per session before the first members read.
 */
export const SyncMembersResponseSchema = z
  .object({ synced: z.boolean(), member_count: z.number(), synced_at: z.string() })
  .passthrough();

/**
 * The "Existing campaign member" typeahead's filtering rule, extracted out
 * of `ScanReview.tsx` (and now shared with `CampaignMemberSearch.tsx`) so it
 * is directly unit-testable.
 *
 * A blank/whitespace-only query returns the WHOLE loaded roster (live-QA
 * round 4, bug 1 — an inversion of the original "search-to-find tool, not a
 * full member browser" stance). This is the fix for "the arrow button for
 * the dropdown doesn't work to view loaded cms": the arrow was never broken,
 * it opened a popup over an empty `items` array, and Glaze hides an empty
 * listbox outright (`has-[[data-slot=list]:empty]:invisible` in
 * `combobox.tsx`'s `popupStyles`). Returning everything makes the arrow a
 * real browse affordance; Glaze's own `max-h-72 overflow-y-auto` popup
 * handles the scrolling, so there is no pagination or virtualization here
 * (launcher's `MemberTable` already renders the same endpoint's full list
 * un-virtualized for the same campaigns).
 *
 * Match dimensions are name / company / SFDC CampaignMember status — the
 * three Spencer named. Status joins the same single lowercased haystack
 * rather than getting its own pass, so "acme responded" can never match:
 * the query is one substring test, exactly as it was for name+company.
 */
export function filterCampaignMembers(members: CampaignMember[], query: string): CampaignMember[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return members;
  return members.filter((m) =>
    `${m.name} ${m.accountName ?? ''} ${m.status ?? ''}`.toLowerCase().includes(trimmed),
  );
}

/**
 * The single source of the "picking a campaign member prefills the form"
 * contract (`ScanReview.tsx`'s `handleLinkMember` and, now, Home's
 * `handleMemberPick`) — both callers use this so the field-mapping can never
 * silently diverge between the two hosts. `accountName ?? ''` / `email ?? ''`
 * mirror the exact coalescing `handleLinkMember` always used; `contactId`
 * passes through as-is (`null` included — Terra never has Lead-backed
 * members, R8).
 */
export function memberPrefillFields(member: CampaignMember): {
  name: string;
  company: string;
  email: string;
  matchedContactId: string | null;
} {
  return {
    name: member.name,
    company: member.accountName ?? '',
    email: member.email ?? '',
    matchedContactId: member.contactId,
  };
}

/**
 * Query key factories for the member sync/read pair above — both hosts
 * (`CampaignMemberSearch.tsx`, and previously `ScanReview.tsx` inline) must
 * import these rather than hand-write the array literal, so the keys can
 * never silently diverge and break the react-query dedupe this design relies
 * on (`ScanReview`'s file header: several mounted review forms can fire the
 * same campaign's member sync at once, and the shared key is what collapses
 * them to one request).
 */
export function campaignMembersSyncQueryKey(campaignId: string | null) {
  return ['lead-scanner', 'campaign-members-sync', campaignId] as const;
}

export function campaignMembersQueryKey(campaignId: string | null) {
  return ['lead-scanner', 'campaign-members', campaignId] as const;
}
